import React, { createContext, useContext, useEffect, useState } from 'react';
import { loadGoogleMapsScript } from '../Utils/googleMapsUtils';

// Create context
const GoogleMapsContext = createContext({
  isLoaded: false,
  loadError: null,
});

/**
 * Hook to access Google Maps loading state
 */
export const useGoogleMaps = () => {
  const context = useContext(GoogleMapsContext);
  if (!context) {
    throw new Error('useGoogleMaps must be used within a GoogleMapsProvider');
  }
  return context;
};

/**
 * Provider component that ensures Google Maps JavaScript API is loaded
 * before rendering children that depend on it
 */
export const GoogleMapsProvider = ({ children }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const initializeGoogleMaps = async () => {
      try {
        // Check if already loaded
        if (window.google && window.google.maps && window.google.maps.places) {
         
          if (mounted) {
            setIsLoaded(true);
          }
          return;
        }

   
        await loadGoogleMapsScript(['places']);
        
        // Double-check that places library is available
        if (window.google && window.google.maps && window.google.maps.places) {
       
          if (mounted) {
            setIsLoaded(true);
          }
        } else {
          throw new Error('Google Maps Places library not available');
        }
      } catch (error) {
        console.error('🗺️ Failed to load Google Maps API:', error);
        if (mounted) {
          setLoadError(error);
        }
      }
    };

    initializeGoogleMaps();

    return () => {
      mounted = false;
    };
  }, []);

  const contextValue = {
    isLoaded,
    loadError,
  };

  return (
    <GoogleMapsContext.Provider value={contextValue}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

/**
 * Higher-order component that wraps a component with Google Maps loading logic
 * The wrapped component will only render after Google Maps is loaded
 */
export const withGoogleMaps = (WrappedComponent, LoadingComponent = null) => {
  return function GoogleMapsWrapper(props) {
    const { isLoaded, loadError } = useGoogleMaps();

    if (loadError) {
      console.error('Google Maps failed to load:', loadError);
      return (
        <div style={{ 
          padding: '16px', 
          color: 'red', 
          backgroundColor: '#fee', 
          border: '1px solid #fcc',
          borderRadius: '4px',
          margin: '8px 0'
        }}>
          <strong>Error:</strong> Unable to load Google Maps. Please check your internet connection and try again.
        </div>
      );
    }

    if (!isLoaded) {
      if (LoadingComponent) {
        return <LoadingComponent />;
      }
      return (
        <div style={{ 
          padding: '16px', 
          textAlign: 'center',
          color: '#666',
          backgroundColor: '#f9f9f9',
          border: '1px solid #ddd',
          borderRadius: '4px',
          margin: '8px 0'
        }}>
          Loading Google Maps...
        </div>
      );
    }

    return <WrappedComponent {...props} />;
  };
};
